import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Calendar, ChevronRight, Plus, Gavel } from "lucide-react";
import AppShell, { getActiveSchemeId } from "@/components/AppShell";
import { useAuth } from "@/auth/AuthContext";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";

const TABS = [
  { key: "upcoming", label: "Upcoming", filter: (m) => ["draft", "notice_sent"].includes(m.status) },
  { key: "in_progress", label: "In progress", filter: (m) => m.status === "in_progress" },
  { key: "completed", label: "Completed", filter: (m) => m.status === "completed" },
];

const STATUS_BADGES = {
  draft: { label: "Draft", bg: "var(--slate-light)", color: "var(--slate)" },
  notice_sent: { label: "Notice sent", bg: "var(--blue-light)", color: "var(--blue-dark)" },
  in_progress: { label: "In progress", bg: "var(--warning-light)", color: "var(--warning)" },
  completed: { label: "Completed", bg: "var(--success-light)", color: "var(--success)" },
  cancelled: { label: "Cancelled", bg: "var(--alert-light)", color: "var(--alert)" },
};

const TYPE_BADGES = {
  AGM: { label: "AGM", color: "var(--navy)" },
  EGM: { label: "EGM", color: "var(--alert)" },
  Committee: { label: "Committee", color: "var(--blue-dark)" },
};

// Sprint 1 Week 1 — Item 1.V — frontend mirror of /app/backend/legal.py NOTICE_PERIOD_MATRIX.
// Keep in sync with backend.
const NOTICE_MATRIX = {
  NSW: { AGM: 7, EGM: 3, Committee: 0 },
  QLD: {
    STANDARD:      { AGM: 21, EGM: 14, Committee: 0 },
    SMALL_SCHEMES: { AGM: 7,  EGM: 7,  Committee: 0 },
    TWO_LOT:       { AGM: 21, EGM: 14, Committee: 0 },
    ACCOMMODATION: { AGM: 21, EGM: 14, Committee: 0 },
    COMMERCIAL:    { AGM: 21, EGM: 14, Committee: 0 },
  },
};
function requiredNoticeDays(state, meeting_type, regulation_module) {
  if (state === "NSW") return NOTICE_MATRIX.NSW[meeting_type] ?? 0;
  if (state === "QLD") {
    const mod = (regulation_module || "STANDARD").toUpperCase();
    return (NOTICE_MATRIX.QLD[mod] || NOTICE_MATRIX.QLD.STANDARD)[meeting_type] ?? 0;
  }
  return 0;
}
function noticeLegislationRef(state) {
  return state === "NSW"
    ? "NSW SSMA 2015 s.18(2) / Schedule 1"
    : state === "QLD"
    ? "QLD BCCM Act 1997 + Regulation Module"
    : "";
}

export default function Meetings() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [schemeId, setSchemeId] = useState(getActiveSchemeId());
  const [scheme, setScheme] = useState(null);
  const [me, setMe] = useState(null);
  const [meetings, setMeetings] = useState([]);
  const [tab, setTab] = useState("upcoming");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const sid = getActiveSchemeId();
      if (!sid) { navigate("/onboarding"); return; }
      try {
        const [s, ms, members] = await Promise.all([
          api.get(`/schemes/${sid}`),
          api.get(`/schemes/${sid}/meetings`),
          api.get(`/schemes/${sid}/members`),
        ]);
        if (!mounted) return;
        setSchemeId(sid);
        setScheme(s.data);
        setMeetings(ms.data);
        const own = members.data.find((m) => m.user_id === user?.user_id);
        setMe(own || (user?.system_role === "platform_admin" ? { role: "chair" } : null));
      } catch (err) {
        toast.error(apiError(err));
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [navigate, user]);

  const isChair = me?.role === "chair" || user?.system_role === "platform_admin";
  const filtered = meetings.filter(TABS.find((t) => t.key === tab).filter);
  const counts = Object.fromEntries(TABS.map((t) => [t.key, meetings.filter(t.filter).length]));

  const createMeeting = async (form) => {
    try {
      const { data } = await api.post(`/schemes/${schemeId}/meetings`, form);
      toast.success("Meeting drafted");
      setOpen(false);
      navigate(`/scheme/meetings/${data.meeting_id}`);
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-5 lg:px-8 py-8 lg:py-10" data-testid="meetings-page">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
          <div>
            <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] mb-2" style={{ color: "var(--blue-dark)" }}>
              <Gavel size={12} /> Meetings
            </span>
            <h1 className="text-2xl font-bold" style={{ color: "var(--navy)" }}>
              {scheme ? `${scheme.name} — meetings` : "Meetings"}
            </h1>
            <p className="text-sm" style={{ color: "var(--slate)" }}>
              Draft notices, capture motions, vote, and publish AI-drafted minutes.
            </p>
          </div>
          {isChair && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button style={{ background: "var(--blue)", color: "white" }} className="font-semibold" data-testid="schedule-meeting-btn">
                  <Plus size={14} className="mr-1.5" /> Schedule meeting
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <MeetingCreateForm scheme={scheme} onSubmit={createMeeting} onCancel={() => setOpen(false)} />
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 mb-5 border-b" style={{ borderColor: "var(--border)" }}>
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`tab-${t.key}`}
              className="px-3 py-2 text-sm font-medium border-b-2 transition-colors"
              style={{
                borderColor: tab === t.key ? "var(--blue)" : "transparent",
                color: tab === t.key ? "var(--navy)" : "var(--slate)",
              }}
            >
              {t.label} <span className="text-xs ml-1 opacity-60">{counts[t.key]}</span>
            </button>
          ))}
        </div>

        {/* List */}
        {loading ? (
          <div className="text-sm py-8 text-center" style={{ color: "var(--slate)" }}>Loading…</div>
        ) : filtered.length === 0 ? (
          <EmptyState tab={tab} isChair={isChair} />
        ) : (
          <div className="space-y-2" data-testid="meetings-list">
            {filtered.map((m) => <MeetingRow key={m.meeting_id} m={m} />)}
          </div>
        )}
      </div>
    </AppShell>
  );
}

function EmptyState({ tab, isChair }) {
  const msg = {
    upcoming: isChair ? "No upcoming meetings. Draft an AGM, EGM, or Committee meeting." : "No upcoming meetings.",
    in_progress: "No meetings are currently in progress.",
    completed: "No completed meetings yet.",
  }[tab];
  return (
    <div className="rounded-xl border bg-white px-6 py-12 text-center" style={{ borderColor: "var(--border)" }} data-testid="meetings-empty">
      <Calendar size={28} className="mx-auto mb-3" style={{ color: "var(--tagline-grey)" }} />
      <p className="text-sm" style={{ color: "var(--slate)" }}>{msg}</p>
    </div>
  );
}

function MeetingRow({ m }) {
  const sb = STATUS_BADGES[m.status];
  const tb = TYPE_BADGES[m.meeting_type];
  const when = new Date(m.scheduled_at);
  return (
    <Link
      to={`/scheme/meetings/${m.meeting_id}`}
      className="block rounded-xl border bg-white px-4 lg:px-5 py-4 hover:shadow-sm transition-shadow"
      style={{ borderColor: "var(--border)" }}
      data-testid={`meeting-row-${m.meeting_id}`}
    >
      <div className="flex items-center gap-3">
        <span className="text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded" style={{ background: "white", border: `1px solid ${tb.color}`, color: tb.color }}>
          {tb.label}
        </span>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm truncate" style={{ color: "var(--navy)" }}>{m.title}</div>
          <div className="text-xs mt-0.5 flex items-center gap-2 flex-wrap" style={{ color: "var(--slate)" }}>
            <span>{when.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", year: "numeric" })}</span>
            <span>·</span>
            <span>{when.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}</span>
            <span>·</span>
            <span className="truncate">{m.location}</span>
            <span>·</span>
            <span>{m.motion_count} motion{m.motion_count === 1 ? "" : "s"}</span>
          </div>
        </div>
        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full" style={{ background: sb.bg, color: sb.color }}>
          {sb.label}
        </span>
        <ChevronRight size={16} style={{ color: "var(--tagline-grey)" }} />
      </div>
    </Link>
  );
}

function MeetingCreateForm({ scheme, onSubmit, onCancel }) {
  const [form, setForm] = useState({
    meeting_type: "Committee",
    title: "",
    scheduled_at: "",
    location: "Common room",
    notice_text: "",
  });
  const set = (k) => (v) => setForm((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));
  // Sprint 1 Week 1 — Item 1.V — live minimum-notice computation.
  const state = scheme?.state || "NSW";
  const regMod = scheme?.regulation_module;
  const minDays = requiredNoticeDays(state, form.meeting_type, regMod);
  const legRef = noticeLegislationRef(state);
  let actualDays = null;
  if (form.scheduled_at) {
    const ms = new Date(form.scheduled_at).getTime() - Date.now();
    actualDays = Math.max(0, Math.floor(ms / 86400000));
  }
  const violates = minDays > 0 && actualDays != null && actualDays < minDays;
  return (
    <>
      <DialogHeader><DialogTitle>Schedule a meeting</DialogTitle></DialogHeader>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const iso = new Date(form.scheduled_at).toISOString();
          onSubmit({ ...form, scheduled_at: iso });
        }}
        className="space-y-3 mt-2"
        data-testid="meeting-create-form"
      >
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--slate)" }}>Type</Label>
          <Select value={form.meeting_type} onValueChange={set("meeting_type")}>
            <SelectTrigger data-testid="meeting-type-select" className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="AGM">AGM — Annual general meeting</SelectItem>
              <SelectItem value="EGM">EGM — Extraordinary general meeting</SelectItem>
              <SelectItem value="Committee">Committee meeting</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--slate)" }}>Title</Label>
          <Input className="mt-1" value={form.title} onChange={set("title")} required data-testid="meeting-title-input" placeholder="e.g. 2026 AGM"/>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--slate)" }}>When</Label>
            <Input type="datetime-local" className="mt-1" value={form.scheduled_at} onChange={set("scheduled_at")} required data-testid="meeting-when-input"/>
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--slate)" }}>Location</Label>
            <Input className="mt-1" value={form.location} onChange={set("location")} required data-testid="meeting-location-input"/>
          </div>
        </div>
        {/* Sprint 1 Week 1 — Item 1.V — live minimum-notice indicator */}
        <div
          className="rounded-md border px-3 py-2 text-xs flex items-start gap-2"
          data-testid="meeting-min-notice-line"
          style={{
            background: violates ? "var(--alert-light)" : "var(--blue-light)",
            borderColor: violates ? "var(--alert)" : "var(--border)",
            color: violates ? "var(--alert)" : "var(--blue-dark)",
          }}
        >
          <span className="font-semibold uppercase tracking-wider text-[10px] mt-0.5">
            {state}{regMod ? ` · ${regMod}` : ""}
          </span>
          <span className="opacity-50 select-none" aria-hidden="true">·</span>
          <div className="flex-1">
            <div data-testid="meeting-min-notice-required">
              Minimum notice for {form.meeting_type}: <strong>{minDays} day{minDays === 1 ? "" : "s"}</strong>
              {actualDays != null && (
                <> · scheduled in <strong data-testid="meeting-min-notice-actual">{actualDays} day{actualDays === 1 ? "" : "s"}</strong></>
              )}
            </div>
            {violates && (
              <div className="mt-1 font-semibold" data-testid="meeting-min-notice-warning">
                Below minimum — backend will reject the “Send notice” action until rescheduled.
              </div>
            )}
            <div className="opacity-70 mt-0.5">{legRef}</div>
          </div>
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--slate)" }}>Notice text (markdown)</Label>
          <Textarea className="mt-1 font-mono text-xs" rows={5} value={form.notice_text} onChange={set("notice_text")} data-testid="meeting-notice-input" placeholder="# Notice of meeting&#10;&#10;Agenda…"/>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
          <Button type="submit" style={{ background: "var(--blue)", color: "white" }} data-testid="meeting-create-submit">Create draft</Button>
        </DialogFooter>
      </form>
    </>
  );
}
